
import { useState } from 'react';
import { format } from 'date-fns';
import { FileCheck, FileDown, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { useDocumentGeneration } from '@/hooks/useDocumentGeneration';

interface GenerateInspectionReportProps {
  state?: string;
  period?: string;
  onReportGenerated?: (reportUrl: string) => void;
}

export function GenerateInspectionReport({ 
  state,
  period, 
  onReportGenerated 
}: GenerateInspectionReportProps) {
  const [selectedState, setSelectedState] = useState<string>(state || '');
  const [selectedPeriod, setSelectedPeriod] = useState<string>(period || '');
  const [reportFormat, setReportFormat] = useState<'pdf' | 'xlsx'>('pdf');
  const [includeOptions, setIncludeOptions] = useState({
    studentInfo: true,
    verificationStatus: true,
    reportingStatus: true,
    courseCompletionDate: true,
    instructorInfo: false,
  });
  
  const { toast } = useToast();
  const { generateDocument, isLoading } = useDocumentGeneration();
  
  const stateOptions = [
    { value: 'FL', label: 'Florida' },
    { value: 'TX', label: 'Texas' },
    { value: 'CA', label: 'California' },
    { value: 'NY', label: 'New York' },
    // Add more states as needed
  ];
  
  const periodOptions = [
    { value: 'q1', label: 'Q1 (Jan-Mar)' },
    { value: 'q2', label: 'Q2 (Apr-Jun)' },
    { value: 'q3', label: 'Q3 (Jul-Sep)' },
    { value: 'q4', label: 'Q4 (Oct-Dec)' },
    // Add more periods as needed
  ];

  const handleGenerateReport = () => {
    if (!selectedState) {
      toast({
        variant: "destructive",
        title: "State Required",
        description: "Please select a state for the inspection report",
      });
      return;
    }
    
    if (!selectedPeriod) {
      toast({
        variant: "destructive",
        title: "Period Required",
        description: "Please select a reporting period",
      });
      return;
    }
    
    // Prepare the data for report generation
    const reportData = {
      state: selectedState,
      period: selectedPeriod,
      format: reportFormat,
      includeOptions,
      generatedAt: new Date().toISOString(),
      reportTitle: `Inspection Report - ${selectedState} - ${selectedPeriod.toUpperCase()} ${new Date().getFullYear()}`
    };
    
    // Generate the document using the useDocumentGeneration hook
    generateDocument({
      template: 'inspection-report',
      documentType: 'regulatory',
      data: reportData,
      fileName: `inspection-report-${selectedState}-${selectedPeriod}-${format(new Date(), 'yyyyMMdd')}.${reportFormat}`
    }, (result) => {
      if (result.success && result.fileUrl) {
        toast({
          title: "Report Generated Successfully",
          description: "Your inspection report is ready for download",
        });
        
        if (onReportGenerated) {
          onReportGenerated(result.fileUrl);
        }
      } else {
        toast({
          variant: "destructive",
          title: "Report Generation Failed",
          description: result.error || "There was an error generating the report",
        });
      }
    });
  };

  const toggleIncludeOption = (option: keyof typeof includeOptions) => {
    setIncludeOptions(prev => ({
      ...prev,
      [option]: !prev[option]
    }));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileCheck className="h-5 w-5" />
          Generate Inspection Report
        </CardTitle>
        <CardDescription>
          Create a formatted report suitable for regulatory inspections and compliance verification
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="state">State</Label>
            <Select 
              value={selectedState} 
              onValueChange={setSelectedState}
            >
              <SelectTrigger id="state">
                <SelectValue placeholder="Select state" />
              </SelectTrigger>
              <SelectContent>
                {stateOptions.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="period">Reporting Period</Label>
            <Select 
              value={selectedPeriod} 
              onValueChange={setSelectedPeriod}
            >
              <SelectTrigger id="period">
                <SelectValue placeholder="Select period" />
              </SelectTrigger>
              <SelectContent>
                {periodOptions.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="format">Report Format</Label>
          <Select 
            value={reportFormat} 
            onValueChange={(value: 'pdf' | 'xlsx') => setReportFormat(value)}
          >
            <SelectTrigger id="format">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pdf">PDF Document</SelectItem>
              <SelectItem value="xlsx">Excel Spreadsheet</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground mt-1">
            PDF is recommended for official submissions. Excel format is suitable for data analysis.
          </p>
        </div>
        
        <div className="space-y-3">
          <Label>Include in Report</Label>
          <div className="grid sm:grid-cols-2 gap-2 mt-1">
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="include-student-info" 
                checked={includeOptions.studentInfo} 
                onCheckedChange={() => toggleIncludeOption('studentInfo')}
              />
              <Label htmlFor="include-student-info" className="text-sm">Student Information</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="include-verification" 
                checked={includeOptions.verificationStatus} 
                onCheckedChange={() => toggleIncludeOption('verificationStatus')}
              />
              <Label htmlFor="include-verification" className="text-sm">Verification Status</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="include-reporting" 
                checked={includeOptions.reportingStatus} 
                onCheckedChange={() => toggleIncludeOption('reportingStatus')}
              />
              <Label htmlFor="include-reporting" className="text-sm">Reporting Status</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="include-completion" 
                checked={includeOptions.courseCompletionDate} 
                onCheckedChange={() => toggleIncludeOption('courseCompletionDate')}
              />
              <Label htmlFor="include-completion" className="text-sm">Course Completion Date</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="include-instructor" 
                checked={includeOptions.instructorInfo} 
                onCheckedChange={() => toggleIncludeOption('instructorInfo')}
              />
              <Label htmlFor="include-instructor" className="text-sm">Instructor Information</Label>
            </div>
          </div>
        </div>
        
        <div className="flex justify-end pt-4">
          <Button 
            onClick={handleGenerateReport}
            disabled={isLoading || !selectedState || !selectedPeriod}
            className="flex items-center gap-2"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <FileDown className="h-4 w-4" />
                Generate Inspection Report
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
